import { History } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useHistoryPages } from '@/api/hooks'
import { Button, EmptyState, Skeleton } from '@/components/ui'
import { PageHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'

import { ClearHistoryDialog } from './ClearHistoryDialog'
import { HistoryRows } from './HistoryRows'
import { SettingsBackLink } from './SettingsBackLink'

/**
 * The whole listening history, which is too long to live in a settings card.
 *
 * A route rather than a dialog: on a phone this is the only shape where the back
 * gesture does the obvious thing, and a list that pages as you scroll needs the
 * room. Clearing is here as well as on the card, because after reading through
 * it is where you would reach for it.
 */
export function HistoryPage() {
  const [confirming, setConfirming] = useState(false)
  const history = useHistoryPages()

  const events = history.data?.pages.flatMap((page) => page.results) ?? []
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = history

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage()
      },
      // Start the next page before the bottom, so the list rarely stalls.
      { rootMargin: '400px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={pageClassName('form')}>
      <div className="mb-3"><SettingsBackLink to="/settings?section=listening" /></div>

      <PageHeader title="Listening history" subtitle="Newest first. Incognito listening is never recorded." actions={events.length > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            Clear
          </Button>
        ) : null} />

      {history.isPending ? (
        <div className="flex flex-col gap-2 rounded-card border border-border bg-surface-sunken p-3">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
            <Skeleton key={row} className="h-8 w-full" />
          ))}
        </div>
      ) : history.isError ? (
        <ErrorNotice busy={history.isFetching} onRetry={() => void history.refetch()}>
          Could not load your history.
        </ErrorNotice>
      ) : events.length === 0 ? (
        <EmptyState
          icon={History}
          title="Nothing recorded yet"
          description="A song is logged once you have listened to thirty seconds of it. Anything you play in incognito is never recorded at all."
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-card border border-border bg-surface-sunken">
            <HistoryRows events={events} />
          </div>

          {hasNextPage ? (
            <div className="flex flex-col items-center gap-2 py-6">
              {/* The observer target sits above the button, so the fallback is
                  only ever reached by someone who scrolled past it. */}
              <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
              <Button
                variant="secondary"
                loading={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : (
            <div className="h-6" />
          )}
        </>
      )}

      {confirming ? <ClearHistoryDialog onClose={() => setConfirming(false)} /> : null}
    </div>
  )
}
